import type { PixelClient } from "../client.js";
import type { ActiveTimeTracker } from "./active-time.js";
import { computeEngagementScore } from "./engagement-score.js";
import { pageContext } from "./page-context.js";
import { scrollDepthPct } from "./scroll-depth.js";
import {
  resolveGalleryElement,
  resolveSectionElement,
  resolveVariantForm,
  type PlatformSelectors,
  type ProductSection,
} from "./selectors.js";
import { attachProductGallery } from "./gallery.js";

export interface ProductPayload {
  product_id: string;
  product_title?: string;
  product_price?: number;
  currency?: string;
  category?: string;
  variant_id?: string;
  /** Canonical product page URL — carried into outreach emails as the "come back" link. */
  product_url?: string;
}

/**
 * Deep product-page tracking: section visibility/dwell, gallery images, variant changes,
 * and a product_engagement summary (with active dwell) on page leave.
 */
export function attachProductEngagement(
  client: PixelClient,
  product: ProductPayload,
  selectors: PlatformSelectors,
  activeTime: ActiveTimeTracker,
): () => void {
  const cleanups: Array<() => void> = [];
  const sections: Record<string, { dwell_sec: number; visible: boolean }> = {};
  const imagesViewed = new Set<number>();
  let imageViewCount = 0;
  let variantChanges = 0;
  let maxScrollDepth = 0;
  let addToCartClicked = false;
  let flushed = false;

  const productId = product.product_id;

  const onCartSubmit = (e: Event) => {
    const form = (e.target as HTMLElement | null)?.closest?.("form.cart, form[action*='/cart/add']");
    if (!form) return;
    addToCartClicked = true;
    activeTime.markActive();
  };
  document.addEventListener("submit", onCartSubmit, true);
  cleanups.push(() => document.removeEventListener("submit", onCartSubmit, true));

  for (const section of selectors.sections) {
    const el = resolveSectionElement(section);
    if (!el) continue;
    el.setAttribute("data-product-section", section.name);
    cleanups.push(observeSection(client, productId, section.name, el, sections, activeTime));
  }

  const gallery = resolveGalleryElement(selectors.gallery);
  if (gallery) {
    cleanups.push(
      attachProductGallery(
        client,
        productId,
        product.product_title,
        gallery,
        imagesViewed,
        () => {
          imageViewCount += 1;
          activeTime.markActive();
        },
      ),
    );
  }

  const variantForm = resolveVariantForm(selectors.variantForm);
  if (variantForm) {
    cleanups.push(watchVariants(client, productId, variantForm, () => {
      variantChanges += 1;
      activeTime.markActive();
    }));
  }

  const onScroll = () => {
    maxScrollDepth = Math.max(maxScrollDepth, scrollDepthPct());
    activeTime.markActive();
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  cleanups.push(() => window.removeEventListener("scroll", onScroll));

  const flush = () => {
    if (flushed) return;
    flushed = true;
    maxScrollDepth = Math.max(maxScrollDepth, scrollDepthPct());
    const { totalDwellSec, activeDwellSec } = activeTime.snapshot();

    const engagementScore = computeEngagementScore({
      totalDwellSec,
      imageViewCount,
      sections,
      variantChanges,
      maxScrollDepth,
      addToCartClicked,
    });

    void client.track(
      "product_engagement",
      {
        product_id: productId,
        ...(product.product_title ? { product_title: product.product_title } : {}),
        ...(product.variant_id ? { variant_id: product.variant_id } : {}),
        total_dwell_sec: totalDwellSec,
        active_dwell_sec: activeDwellSec,
        max_scroll_depth: maxScrollDepth,
        image_view_count: imageViewCount,
        images_viewed: [...imagesViewed].sort((a, b) => a - b),
        variant_changes: variantChanges,
        sections,
        interactions: {
          add_to_cart_clicked: addToCartClicked,
          quantity_changed: false,
          size_chart_opened: false,
          zoom_clicked: false,
        },
        engagement_score: engagementScore,
      },
      pageContext(),
    );
  };

  const onHide = () => {
    if (document.visibilityState === "hidden") flush();
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", flush);
  cleanups.push(() => {
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", flush);
  });

  return () => {
    flush();
    for (const fn of cleanups) fn();
  };
}

function observeSection(
  client: PixelClient,
  productId: string,
  sectionName: string,
  el: Element,
  sections: Record<string, { dwell_sec: number; visible: boolean }>,
  activeTime: ActiveTimeTracker,
): () => void {
  let enteredAt: number | null = null;

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry) return;
      const now = Date.now();

      if (entry.isIntersecting && enteredAt === null) {
        enteredAt = now;
        activeTime.markActive();
        sections[sectionName] = { dwell_sec: sections[sectionName]?.dwell_sec ?? 0, visible: true };
        void client.track(
          "product_section_visible",
          { product_id: productId, section: sectionName },
          pageContext(now),
        );
      } else if (!entry.isIntersecting && enteredAt !== null) {
        const dwellSec = Math.round((now - enteredAt) / 1000);
        enteredAt = null;
        const prev = sections[sectionName]?.dwell_sec ?? 0;
        sections[sectionName] = { dwell_sec: prev + dwellSec, visible: false };
        activeTime.markActive();
        void client.track(
          "product_section_dwell",
          { product_id: productId, section: sectionName, dwell_sec: dwellSec },
          pageContext(now),
        );
      }
    },
    { threshold: 0.5 },
  );

  observer.observe(el);
  return () => observer.disconnect();
}

function watchVariants(
  client: PixelClient,
  productId: string,
  form: HTMLFormElement,
  onChange: () => void,
): () => void {
  const onChangeEvent = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | null;
    if (!target || !("name" in target)) return;

    onChange();
    const optionName = target.name.replace(/^attribute_/, "");
    void client.track(
      "product_variant_changed",
      {
        product_id: productId,
        option_name: optionName,
        value: target.value,
        ...(target.dataset.variationId ? { variant_id: target.dataset.variationId } : {}),
      },
      pageContext(),
    );
  };

  form.addEventListener("change", onChangeEvent);
  return () => form.removeEventListener("change", onChangeEvent);
}
